import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Mail, MessageSquare } from 'lucide-react';
import { usePrefs } from '../../hooks/hooks.prefs';
import TechIcon from '../../components/fx/TechIcon';

const NAV_GROUPS = [
    {
        title: { fr: "Navigation", en: "Navigation" },
        links: [
            { label: { fr: "Services", en: "Services" }, href: "/#services", sectionId: "services" },
            { label: { fr: "Stack tech", en: "Tech stack" }, href: "/#techstack", sectionId: "techstack" },
            { label: { fr: "Projets", en: "Work" }, href: "/#projects", sectionId: "projects" },
            { label: { fr: "À propos", en: "About" }, href: "/#about", sectionId: "about" },
            { label: { fr: "Playground", en: "Playground" }, href: "/playground" },
            { label: { fr: "Contact", en: "Contact" }, href: "/contact" },
        ],
    },
    {
        title: { fr: "Produits", en: "Products" },
        links: [
            { label: "Kalanyoro", href: "https://kalanyoro.savantech.org" },
            { label: "Sav Space", href: "https://sav-space.savantech.org" },
            { label: "Aurea", href: "https://aurea-bf.com" },
            { label: "Medic'Help", href: "https://medichelp.savantech.org" },
            { label: "SavanTech Invest", href: "https://invest.savantech.org" },
        ],
    },
    {
        title: { fr: "Ressources", en: "Resources" },
        links: [
            { label: { fr: "Sandbox en ligne", en: "Online sandbox" }, href: "/playground" },
            { label: { fr: "Guide de développement", en: "Development guide" }, href: "/playground?tab=guide" },
            { label: { fr: "Astuces", en: "Tips" }, href: "/playground?tab=tips" },
            { label: { fr: "Templates squeleton", en: "squeleton templates" }, href: "https://github.com/Yameogo123/squeleton" },
            { label: "fast-stack-forge", href: "https://pypi.org/project/fast-stack-forge/" },
        ],
    },
];

const external = (href) => /^https?:/.test(href);

export default function ViewFooter() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t, tx } = usePrefs();

    const handleNavClick = (e, link) => {
        if (external(link.href)) return; // lien externe : nouvel onglet
        e.preventDefault();
        if (link.sectionId) {
            const go = () => document.getElementById(link.sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (location.pathname !== '/') { navigate('/'); setTimeout(go, 300); } else go();
        } else if (link.href === '/') {
            if (location.pathname !== '/') navigate('/'); else window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate(link.href);
            window.scrollTo({ top: 0 });
        }
    };

    return (
        <footer className="relative bg-duna-bg border-t border-duna-border overflow-hidden">
            <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[900px] h-[300px] bg-[radial-gradient(ellipse,rgba(20,184,166,0.10),transparent_70%)]" aria-hidden="true" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="py-14 sm:py-16 grid grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-10 sm:gap-12">
                    <div className="col-span-2">
                        <a href="/" onClick={(e) => handleNavClick(e, { href: '/' })} className="inline-flex items-center gap-3 mb-6 no-underline group">
                            <img src="/assets/images/logo.png" alt="" width="36" height="36" className="w-9 h-9 object-contain transition-transform group-hover:rotate-[-8deg]" />
                            <span className="font-bold text-lg text-duna-text">Savan<span className="text-duna-accent">Tech</span></span>
                        </a>
                        <p className="text-duna-muted text-sm leading-relaxed mb-6 max-w-xs">
                            {t("Nous transformons vos données en intelligence, vos idées en applications et vos ambitions en réalité technologique.", "We turn your data into intelligence, your ideas into apps and your ambitions into technology.")}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-6">
                            <a href="mailto:savantech25@gmail.com" className="inline-flex items-center gap-2 h-10 px-3.5 rounded-xl border border-duna-border text-sm text-duna-text no-underline hover:bg-duna-pill transition-colors"><Mail size={15} /> Email</a>
                            <a href="https://wa.me/22604894780" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 h-10 px-3.5 rounded-xl border border-duna-border text-sm text-duna-text no-underline hover:bg-duna-pill transition-colors"><MessageSquare size={15} /> WhatsApp</a>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-duna-accent animate-pulse" />
                            <span className="font-mono text-xs text-duna-subtle">Ouagadougou · Lyon · Remote</span>
                        </div>
                    </div>

                    {NAV_GROUPS.map(group => (
                        <div key={tx(group.title)}>
                            <h2 className="font-mono text-[11px] font-bold text-duna-subtle uppercase tracking-widest mb-5">{tx(group.title)}</h2>
                            <ul className="space-y-1 list-none p-0 m-0">
                                {group.links.map(link => (
                                    <li key={link.href}>
                                        <a
                                            href={link.href}
                                            onClick={(e) => handleNavClick(e, link)}
                                            {...(external(link.href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                            className="group inline-flex items-center gap-1 min-h-[32px] text-sm text-duna-muted hover:text-duna-text transition-colors no-underline"
                                        >
                                            {tx(link.label)}
                                            {external(link.href) && <ArrowUpRight size={12} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="h-px bg-duna-border" />

                <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-xs font-mono text-duna-subtle">
                        © {new Date().getFullYear()} SavanTech · <span className="text-duna-accent">{t('Transformer la donnée en réussite', 'Transforming data into success')}</span>
                    </p>
                    <div className="flex items-center gap-3 text-[10px] font-mono text-duna-subtle uppercase tracking-widest">
                        {t('Construit avec', 'Built with')}
                        {['React', 'Tailwind CSS', 'Firebase'].map((n) => <span key={n} title={n} className="inline-flex"><TechIcon name={n} size={15} /></span>)}
                    </div>
                </div>
            </div>
        </footer>
    );
}
